import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic } from 'lucide-react';
import { useApp } from '../context/AppContext.jsx';
import {
  audioSessionManager,
  MicRecorder,
  TranscriptionSource,
} from '../services/audio/index.js';
import FoxMark from '../components/shared/FoxMark.jsx';
import NavMenu from '../components/shared/NavMenu.jsx';

const PAPER = '#F2EBDC';
const INK = '#1B1814';
const GOLD = '#A67C3A';

export default function Journal() {
  const navigate = useNavigate();
  const {
    journalEntries,
    addJournalEntry,
    highlights,
    updateHighlightNote,
    daysUnlocked,
    getCurrentWeekNumber,
  } = useApp();

  const [text, setText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [voiceError, setVoiceError] = useState(null);
  const recorderRef = useRef(null);

  // Inline note editor — only one highlight may be in edit mode at a
  // time. null when no card is being edited.
  const [editingHighlightId, setEditingHighlightId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');

  // Voice input INSIDE the inline note editor. Mirrors the journal
  // entry voice flow (handleVoiceToggle above) but scoped to the note
  // editor — single-active because editingHighlightId is itself
  // single-active.
  const [isRecordingNote, setIsRecordingNote] = useState(false);
  const [isTranscribingNote, setIsTranscribingNote] = useState(false);
  const [noteVoiceError, setNoteVoiceError] = useState(null);
  const noteRecorderRef = useRef(null);

  function handleStartEditNote(item) {
    setEditingHighlightId(item.id);
    setEditingNoteText(item.note || '');
  }

  function handleCancelEditNote() {
    // If a note recording is in flight, cancel it so the mic stream is
    // released and the user isn't stuck with a half-captured blob.
    if (noteRecorderRef.current) {
      try {
        noteRecorderRef.current.cancel();
      } catch {}
      noteRecorderRef.current = null;
    }
    setIsRecordingNote(false);
    setIsTranscribingNote(false);
    setNoteVoiceError(null);
    setEditingHighlightId(null);
    setEditingNoteText('');
  }

  function handleSaveNote(item) {
    const trimmed = editingNoteText.trim();
    updateHighlightNote(item.id, trimmed || null);
    setEditingHighlightId(null);
    setEditingNoteText('');
  }

  async function handleNoteVoiceToggle() {
    if (!isRecordingNote) {
      setNoteVoiceError(null);
      await audioSessionManager.unlock();
      const rec = new MicRecorder(audioSessionManager);
      noteRecorderRef.current = rec;
      try {
        await rec.start();
      } catch (err) {
        const msg =
          err?.name === 'NotAllowedError'
            ? 'Microphone access denied. Enable it in your browser settings to record.'
            : "Couldn't start recording. Try again in a moment.";
        setNoteVoiceError(msg);
        noteRecorderRef.current = null;
        return;
      }
      setIsRecordingNote(true);
    } else {
      setIsRecordingNote(false);
      setIsTranscribingNote(true);
      try {
        const { audioBlob } = await noteRecorderRef.current.stop();
        noteRecorderRef.current = null;
        if (!audioBlob) {
          // cancelled / no audio captured
          setIsTranscribingNote(false);
          return;
        }
        const { text: transcript } = await new TranscriptionSource().transcribe(
          audioBlob
        );
        setEditingNoteText((prev) =>
          prev.trim() ? `${prev.trimEnd()} ${transcript}` : transcript
        );
      } catch (err) {
        console.warn('[Journal/Note] voice transcribe failed:', err?.message);
        setNoteVoiceError(
          "Couldn't transcribe that recording. Please try again."
        );
      } finally {
        setIsTranscribingNote(false);
        noteRecorderRef.current = null;
      }
    }
  }

  // Auto-pick the user's current active day from AppContext:
  // daysUnlocked tracks how many days are accessible within the current
  // week (capped 1-5), so the highest unlocked value is the day the user
  // is most likely working on. getCurrentWeekNumber returns the lowest
  // week that still has incomplete days, falling back to the highest
  // available week. Both match the semantics StudyPlan / DayDetail use
  // to surface "where the user is right now".
  const currentDayNumber = Math.max(1, Math.min(daysUnlocked || 1, 5));
  const currentWeekNumber = getCurrentWeekNumber();
  // Save is disabled when there's nothing typed, while a recording is in
  // flight (the transcript hasn't landed yet), or while we're waiting on
  // the transcription network round-trip.
  const canSave =
    text.trim().length > 0 && !isRecording && !isTranscribing;

  function handleSave() {
    if (!canSave) return;
    addJournalEntry(currentDayNumber, text.trim(), currentWeekNumber);
    setText('');
  }

  async function handleVoiceToggle() {
    if (!isRecording) {
      setVoiceError(null);
      await audioSessionManager.unlock();
      const rec = new MicRecorder(audioSessionManager);
      recorderRef.current = rec;
      try {
        await rec.start();
      } catch (err) {
        const msg =
          err?.name === 'NotAllowedError'
            ? 'Microphone access denied. Enable it in your browser settings to record.'
            : "Couldn't start recording. Try again in a moment.";
        setVoiceError(msg);
        recorderRef.current = null;
        return;
      }
      setIsRecording(true);
    } else {
      setIsRecording(false);
      setIsTranscribing(true);
      try {
        const { audioBlob } = await recorderRef.current.stop();
        recorderRef.current = null;
        if (!audioBlob) {
          // cancelled / no audio captured
          setIsTranscribing(false);
          return;
        }
        const { text: transcript } = await new TranscriptionSource().transcribe(
          audioBlob
        );
        setText((prev) =>
          prev.trim() ? `${prev.trimEnd()} ${transcript}` : transcript
        );
      } catch (err) {
        console.warn('[Journal] voice transcribe failed:', err?.message);
        setVoiceError("Couldn't transcribe that recording. Please try again.");
      } finally {
        setIsTranscribing(false);
        recorderRef.current = null;
      }
    }
  }

  // Belt-and-suspenders cleanup — if the user navigates away mid-record,
  // cancel the recorder so the mic stream is released.
  useEffect(() => {
    return () => {
      if (recorderRef.current) {
        try {
          recorderRef.current.cancel();
        } catch {}
        recorderRef.current = null;
      }
    };
  }, []);

  // Same cleanup pattern for the note-editor recorder.
  useEffect(() => {
    return () => {
      if (noteRecorderRef.current) {
        try {
          noteRecorderRef.current.cancel();
        } catch {}
        noteRecorderRef.current = null;
      }
    };
  }, []);

  // Auto-clear voiceError after 4s so it doesn't linger past relevance.
  useEffect(() => {
    if (!voiceError) return;
    const t = setTimeout(() => setVoiceError(null), 4000);
    return () => clearTimeout(t);
  }, [voiceError]);

  // Auto-clear noteVoiceError — same 4s window as the journal flow.
  useEffect(() => {
    if (!noteVoiceError) return;
    const t = setTimeout(() => setNoteVoiceError(null), 4000);
    return () => clearTimeout(t);
  }, [noteVoiceError]);

  // Unified feed of user reflections — journal entries the user wrote
  // and passages they highlighted from the daily study. Each item is
  // tagged with a `type` so the render loop can dispatch styling.
  // Sort descending by createdAt so the most recent reflection is at
  // the top, mixing both kinds chronologically.
  const combinedEntries = [
    ...(journalEntries || []).map((e) => ({ type: 'entry', ...e })),
    ...(highlights || []).map((h) => ({ type: 'highlight', ...h })),
  ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  function formatDate(iso) {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  return (
    <div style={{ minHeight: '100vh', background: PAPER }}>
      <div
        style={{
          padding: '20px',
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          borderBottom: '1px solid rgba(166,124,58,0.15)',
        }}
      >
        <button
          type="button"
          onClick={() => navigate('/study')}
          style={{
            justifySelf: 'start',
            background: 'none',
            border: 'none',
            color: GOLD,
            fontSize: 22,
            cursor: 'pointer',
            padding: 0,
          }}
        >
          ‹
        </button>
        <span
          style={{
            fontSize: 13,
            color: INK,
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
          }}
        >
          My Journal
        </span>
        <div
          style={{
            justifySelf: 'end',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <FoxMark variant="gold" size={24} />
          <NavMenu />
        </div>
      </div>

      <div
        style={{
          maxWidth: 480,
          margin: '0 auto',
          padding: '24px 20px 80px',
        }}
      >
        {/* Compose surface — auto-attaches the entry to the user's current
            active day/week (no picker UI). */}
        <div
          style={{
            background: 'white',
            border: '1px solid rgba(166,124,58,0.25)',
            borderRadius: 12,
            padding: '16px 18px',
            marginBottom: 24,
          }}
        >
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            placeholder="Write what's on your heart…"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: PAPER,
              border: '1px solid rgba(166,124,58,0.15)',
              borderRadius: 8,
              padding: '12px 14px',
              fontFamily: 'Georgia, serif',
              fontSize: 15,
              color: INK,
              lineHeight: 1.7,
              resize: 'vertical',
              outline: 'none',
              minHeight: 110,
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'rgba(166,124,58,0.55)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'rgba(166,124,58,0.15)';
            }}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 10,
              gap: 10,
            }}
          >
            <button
              type="button"
              onClick={handleVoiceToggle}
              disabled={isTranscribing}
              aria-label={
                isRecording
                  ? 'Stop recording'
                  : isTranscribing
                  ? 'Transcribing'
                  : 'Start recording'
              }
              style={{
                background: isRecording ? '#8B6914' : 'transparent',
                color: isRecording ? PAPER : GOLD,
                border: `1px solid ${
                  isRecording ? '#8B6914' : 'rgba(166,124,58,0.45)'
                }`,
                borderRadius: 8,
                padding: '8px 14px',
                fontSize: 13,
                fontFamily: 'Arial, sans-serif',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                fontWeight: 600,
                cursor: isTranscribing ? 'default' : 'pointer',
                opacity: isTranscribing ? 0.6 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                transition:
                  'background 150ms, border-color 150ms, opacity 150ms',
                animation: isRecording
                  ? 'journalMicPulse 1.6s ease-in-out infinite'
                  : 'none',
              }}
            >
              {isTranscribing ? (
                <span>Transcribing…</span>
              ) : (
                <>
                  <Mic size={16} strokeWidth={isRecording ? 2.4 : 1.8} />
                  <span>{isRecording ? 'Stop' : 'Record'}</span>
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              style={{
                background: canSave ? GOLD : 'rgba(166,124,58,0.25)',
                color: PAPER,
                border: 'none',
                borderRadius: 8,
                padding: '8px 18px',
                fontSize: 13,
                fontFamily: 'Arial, sans-serif',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                fontWeight: 600,
                cursor: canSave ? 'pointer' : 'default',
                opacity: canSave ? 1 : 0.6,
                transition: 'background 150ms, opacity 150ms',
              }}
            >
              Save entry
            </button>
          </div>
          {voiceError && (
            <p
              style={{
                marginTop: 10,
                marginBottom: 0,
                fontFamily: 'Georgia, serif',
                fontStyle: 'italic',
                fontSize: 13,
                color: '#8B6914',
                lineHeight: 1.5,
              }}
            >
              {voiceError}
            </p>
          )}
        </div>
        <style>{`
          @keyframes journalMicPulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(139,105,20,0.55); }
            50% { box-shadow: 0 0 0 10px rgba(139,105,20,0); }
          }
        `}</style>

        {combinedEntries.length === 0 ? (
          <div style={{ textAlign: 'center', paddingTop: 40 }}>
            <p
              style={{
                fontFamily: 'Georgia, serif',
                fontSize: 16,
                color: INK,
                opacity: 0.4,
                fontStyle: 'italic',
                lineHeight: 1.7,
              }}
            >
              Your journal entries and highlighted passages will appear here.
              Write one above, or highlight a passage as you read.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {combinedEntries.map((item) => (
              <div
                key={`${item.type}-${item.id || item.createdAt}`}
                style={{
                  background: 'white',
                  border: '1px solid rgba(166,124,58,0.15)',
                  borderRadius: 12,
                  padding: '16px 20px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 10,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        color: GOLD,
                        fontFamily: 'Arial, sans-serif',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                      }}
                    >
                      Day {item.dayNumber}
                    </span>
                    {item.type === 'highlight' && (
                      <span
                        style={{
                          fontSize: 10,
                          color: PAPER,
                          background: '#8B6914',
                          fontFamily: 'Arial, sans-serif',
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: 999,
                        }}
                      >
                        Highlight
                      </span>
                    )}
                  </div>
                  <span
                    style={{
                      fontSize: 11,
                      color: INK,
                      opacity: 0.4,
                      fontFamily: 'Arial, sans-serif',
                    }}
                  >
                    {formatDate(item.createdAt)}
                  </span>
                </div>
                {item.type === 'entry' ? (
                  <p
                    style={{
                      fontSize: 15,
                      color: INK,
                      fontFamily: 'Georgia, serif',
                      lineHeight: 1.7,
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {item.entry}
                  </p>
                ) : (
                  <>
                    <p
                      style={{
                        fontSize: 15,
                        color: INK,
                        fontFamily: 'Georgia, serif',
                        fontStyle: 'italic',
                        lineHeight: 1.7,
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {'“'}{item.text}{'”'}
                    </p>
                    {item.note &&
                      item.note.trim() &&
                      editingHighlightId !== item.id && (
                        <div
                          style={{
                            marginTop: 12,
                            borderLeft: '3px solid rgba(166,124,58,0.4)',
                            paddingLeft: 12,
                          }}
                        >
                          <p
                            style={{
                              fontFamily: 'Georgia, serif',
                              fontSize: 14,
                              color: 'rgba(27,24,20,0.75)',
                              lineHeight: 1.6,
                              margin: 0,
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {item.note}
                          </p>
                        </div>
                      )}
                    {editingHighlightId === item.id ? (
                      <div style={{ marginTop: 12 }}>
                        <textarea
                          value={editingNoteText}
                          onChange={(e) => setEditingNoteText(e.target.value)}
                          rows={5}
                          placeholder="What does this passage say to you?"
                          style={{
                            width: '100%',
                            boxSizing: 'border-box',
                            background: PAPER,
                            border: '1px solid rgba(166,124,58,0.15)',
                            borderRadius: 8,
                            padding: '10px 12px',
                            fontFamily: 'Georgia, serif',
                            fontSize: 14,
                            color: INK,
                            lineHeight: 1.6,
                            resize: 'vertical',
                            outline: 'none',
                            minHeight: 100,
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor =
                              'rgba(166,124,58,0.55)';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor =
                              'rgba(166,124,58,0.15)';
                          }}
                        />
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 8,
                            marginTop: 8,
                          }}
                        >
                          <button
                            type="button"
                            onClick={handleNoteVoiceToggle}
                            disabled={isTranscribingNote}
                            aria-label={
                              isRecordingNote
                                ? 'Stop recording'
                                : isTranscribingNote
                                ? 'Transcribing'
                                : 'Start recording'
                            }
                            style={{
                              background: isRecordingNote
                                ? '#8B6914'
                                : 'transparent',
                              color: isRecordingNote ? PAPER : GOLD,
                              border: `1px solid ${
                                isRecordingNote
                                  ? '#8B6914'
                                  : 'rgba(166,124,58,0.45)'
                              }`,
                              borderRadius: 8,
                              padding: '6px 12px',
                              fontSize: 12,
                              fontFamily: 'Arial, sans-serif',
                              letterSpacing: '0.06em',
                              textTransform: 'uppercase',
                              fontWeight: 600,
                              cursor: isTranscribingNote ? 'default' : 'pointer',
                              opacity: isTranscribingNote ? 0.6 : 1,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              transition:
                                'background 150ms, border-color 150ms, opacity 150ms',
                            }}
                          >
                            {isTranscribingNote ? (
                              <span>Transcribing…</span>
                            ) : (
                              <>
                                <Mic
                                  size={14}
                                  strokeWidth={isRecordingNote ? 2.4 : 1.8}
                                />
                                <span>
                                  {isRecordingNote ? 'Stop' : 'Record'}
                                </span>
                              </>
                            )}
                          </button>
                          <div style={{ display: 'flex', gap: 8 }}>
                            {(() => {
                              const busy =
                                isRecordingNote || isTranscribingNote;
                              return (
                                <button
                                  type="button"
                                  onClick={handleCancelEditNote}
                                  disabled={busy}
                                  style={{
                                    background: 'transparent',
                                    color: GOLD,
                                    border:
                                      '1px solid rgba(166,124,58,0.45)',
                                    borderRadius: 8,
                                    padding: '6px 14px',
                                    fontSize: 12,
                                    fontFamily: 'Arial, sans-serif',
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                    cursor: busy ? 'default' : 'pointer',
                                    opacity: busy ? 0.5 : 1,
                                  }}
                                >
                                  Cancel
                                </button>
                              );
                            })()}
                            {(() => {
                              const trimmed = editingNoteText.trim();
                              const unchanged = trimmed === (item.note || '');
                              const disabled =
                                unchanged ||
                                isRecordingNote ||
                                isTranscribingNote;
                              return (
                                <button
                                  type="button"
                                  onClick={() => handleSaveNote(item)}
                                  disabled={disabled}
                                  style={{
                                    background: disabled
                                      ? 'rgba(166,124,58,0.25)'
                                      : GOLD,
                                    color: PAPER,
                                    border: 'none',
                                    borderRadius: 8,
                                    padding: '6px 14px',
                                    fontSize: 12,
                                    fontFamily: 'Arial, sans-serif',
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                    cursor: disabled ? 'default' : 'pointer',
                                    opacity: disabled ? 0.6 : 1,
                                  }}
                                >
                                  Save
                                </button>
                              );
                            })()}
                          </div>
                        </div>
                        {noteVoiceError && (
                          <p
                            style={{
                              marginTop: 8,
                              marginBottom: 0,
                              fontFamily: 'Georgia, serif',
                              fontStyle: 'italic',
                              fontSize: 12,
                              color: '#8B6914',
                              lineHeight: 1.5,
                            }}
                          >
                            {noteVoiceError}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          marginTop: 10,
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => handleStartEditNote(item)}
                          style={{
                            background: 'transparent',
                            color: GOLD,
                            border: 'none',
                            padding: '4px 0',
                            fontSize: 12,
                            fontFamily: 'Arial, sans-serif',
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          {item.note && item.note.trim()
                            ? 'Edit note'
                            : 'Add note'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
